import { useState, type ReactNode } from "react";
import "./AIFoundations.css";

const Frame = ({ eyebrow, title, children }: { eyebrow: string; title: string; children: ReactNode }) => (
  <div className="fundamentals-slide"><p className="fundamentals-eyebrow">{eyebrow}</p><h1 className="fundamentals-heading">{title}</h1>{children}</div>
);
const PRIORITY_LOOP = [
  { label: "Give the task", actor: "You → harness → model", body: "Ask for a ranked daily priority brief. The harness supplies your request, approved standing context, and Chief of Staff Skill." },
  { label: "Request a tool", actor: "Model → harness → tool", body: "The model requests the approved calendar. The harness checks access and executes the calendar-reading tool. The Skill does not grant permission." },
  { label: "Return the evidence", actor: "Tool → harness → model", body: "The tool returns calendar evidence. The harness includes it in the next model input. A connected calendar alone does not mean its contents were read." },
  { label: "Review the brief", actor: "Model → harness → you", body: "The model produces ranked priorities with source links and open questions. A person checks each priority before changing a record or sending anything." },
];
const UPDATE_LOOP = [
  { label: "Give the task", actor: "You → harness → model", body: "You ask for a weekly update. The harness supplies your request, relevant context, and the status-update skill instructions." },
  { label: "Request a tool", actor: "Model → harness → tool", body: "The model requests the meeting notes. The harness checks access and runs the document-reading tool." },
  { label: "Return the notes", actor: "Tool → harness → model", body: "The tool returns the notes. The harness adds them to the next model input so the model can use that evidence." },
  { label: "Review the draft", actor: "Model → harness → you", body: "The model produces the update. The application shows the draft, and you check facts and missing information before sharing." },
];

export const ToolLoop = ({ printMode = false, chiefOfStaff = false }: { printMode?: boolean; chiefOfStaff?: boolean }) => {
  const LOOP_STEPS = chiefOfStaff ? PRIORITY_LOOP : UPDATE_LOOP;
  const [active, setActive] = useState(0);
  const step = LOOP_STEPS[active];
  return (
    <Frame eyebrow="Follow one run" title="A tool result becomes new input.">
      <p className="fundamentals-lede">The harness connects the steps. The model may need more than one turn.</p>
      {printMode ? (
        <ol className="fundamentals-loop-print">{LOOP_STEPS.map((item) => <li key={item.label}><strong>{item.label}</strong><p>{item.body}</p></li>)}</ol>
      ) : (
        <>
          <div className="fundamentals-loop-controls" role="group" aria-label="Explore the four steps of a tool-assisted update">
            {LOOP_STEPS.map((item, index) => <button key={item.label} type="button" aria-pressed={active === index} aria-controls="fundamentals-loop-detail" onClick={() => setActive(index)}><span>0{index + 1}</span>{item.label}</button>)}
          </div>
          <div id="fundamentals-loop-detail" className="fundamentals-loop-detail" aria-live="polite" aria-atomic="true">
            <p className="fundamentals-label">{step.actor}</p><p>{step.body}</p>
          </div>
        </>
      )}
      <p className="fundamentals-note">A request to use a tool is model output. The surrounding software executes the operation and returns its result.</p>
    </Frame>
  );
};
